import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FormInput from '@ui/forms/Input/FormInput'
import FormSelect from '@ui/forms/Select/FormSelect'
import InputsSection from '@ui/forms/InputsSection'
import FormSection, { FormRow } from './FormSection'
import { getCountryOptions } from '../countries'
import {
  CofidisMonthlyApplicationFormValues,
  CofidisSectionProps,
} from '../types'
import {
  HomeTypeValues,
  HomeType,
  WayTypeValues,
  HomeExpensesConfig,
} from '../enums'

const HomeExpensesDefaultConfig = {
  required: false,
  label: 'default',
}

export default function ResidenceSection({
  fieldSuccess,
}: CofidisSectionProps) {
  const { t, i18n } = useTranslation(['cofidis'])
  const { control } = useFormContext<CofidisMonthlyApplicationFormValues>()
  const homeTypeOptions = useMemo(
    () =>
      HomeTypeValues.map((option) => ({
        value: option,
        label: t(`cofidis:homeType.options.${HomeType[option]}.label`),
      })),
    [t]
  )
  const wayTypeOptions = useMemo(
    () =>
      WayTypeValues.map((value) => ({
        value,
        label: t(`cofidis:wayType.options.${value}.label`),
      })),
    [t]
  )
  const countryOptions = useMemo(
    () => getCountryOptions(i18n.resolvedLanguage ?? i18n.language ?? 'en'),
    [i18n.resolvedLanguage, i18n.language]
  )

  const homeType = useWatch({ control, name: 'homeType' })
  const homeExpensesConfig = homeType
    ? HomeExpensesConfig[homeType] ?? HomeExpensesDefaultConfig
    : HomeExpensesDefaultConfig
  return (
    <InputsSection group>
      <FormSection>
        <FormRow>
          <FormSelect
            required
            labelVariant="top"
            control={control}
            name="wayType"
            isSuccess={fieldSuccess?.wayType}
            label={t('cofidis:wayType.label')}
            options={wayTypeOptions}
          />

          <FormInput
            required
            labelVariant="top"
            name="wayDescription"
            isSuccess={fieldSuccess?.wayDescription}
            label={t('cofidis:wayDescription.label')}
            autoCapitalize="words"
            next="wayNumber"
          />
        </FormRow>
        <FormRow>
          <FormInput
            required
            labelVariant="top"
            name="wayNumber"
            isSuccess={fieldSuccess?.wayNumber}
            label={t('cofidis:wayNumber.label')}
            next="floor"
          />

          <FormInput
            required={false}
            labelVariant="top"
            name="floor"
            isSuccess={fieldSuccess?.floor}
            label={t('cofidis:floor.label')}
            autoCapitalize="characters"
            next="letter"
          />

          <FormInput
            required={false}
            labelVariant="top"
            name="letter"
            isSuccess={fieldSuccess?.letter}
            label={t('cofidis:letter.label')}
            autoCapitalize="characters"
            next="additionalAddress"
          />
        </FormRow>
        <FormRow>
          <FormInput
            required={false}
            labelVariant="top"
            name="additionalAddress"
            isSuccess={fieldSuccess?.additionalAddress}
            label={t('cofidis:additionalAddress.label')}
            autoCapitalize="words"
            next="zipCode"
          />
        </FormRow>
        <FormRow>
          <FormInput
            required
            labelVariant="top"
            name="zipCode"
            isSuccess={fieldSuccess?.zipCode}
            label={t('cofidis:zipCode.label')}
            next="town"
          />
          <FormInput
            required
            labelVariant="top"
            name="town"
            isSuccess={fieldSuccess?.town}
            label={t('cofidis:town.label')}
            autoCapitalize="words"
            next="country"
          />
        </FormRow>
        <FormRow>
          <FormSelect
            required
            labelVariant="top"
            control={control}
            name="country"
            isSuccess={fieldSuccess?.country}
            label={t('cofidis:country.label')}
            options={countryOptions}
          />
          <FormSelect
            required
            labelVariant="top"
            control={control}
            name="homeType"
            isSuccess={fieldSuccess?.homeType}
            label={t('cofidis:homeType.label')}
            options={homeTypeOptions}
          />
        </FormRow>
        <FormRow>
          <FormInput
            required
            inputMode="numeric"
            labelVariant="top"
            name="entranceDate"
            isSuccess={fieldSuccess?.entranceDate}
            label={t('cofidis:entranceDate.label')}
            placeholder="YYYY"
            maxLength={4}
          />
          <FormInput
            required={homeExpensesConfig.required}
            labelVariant="top"
            control={control}
            name="homeExpenses"
            isSuccess={fieldSuccess?.homeExpenses}
            label={t(`cofidis:homeExpenses.label.${homeExpensesConfig.label}`)}
            inputMode="decimal"
            textEnd={t('cofidis:homeExpenses.end')}
          />
        </FormRow>
      </FormSection>
    </InputsSection>
  )
}
